/**
 * N28 « Prochaine action » — règles PURES (IA v4 § 4.2.2, BA-03, AC-P2 ; ARB-r2-20 ; planche
 * `VueProjetEtats`, tableau « ordre des règles par rôle »).
 *
 *  - la première règle non vide du rôle l'emporte ;
 *  - une action REFUSÉE avant le clic est sautée (et dite comme telle) ;
 *  - à règle égale, la plus ANCIENNE gagne ;
 *  - rien pour le rôle : le rôle qui peut agir est nommé.
 *
 * Calcul client, sur des points d'accès existants (Boîte d'attention, missions, campagnes,
 * déploiements, versions, aperçus, backlog, budget d'erreur). L'instant représenté est un
 * paramètre (ARB-r2-20) : jamais `Date.now()` dans les règles.
 */
import { lireInstant } from './horodatage'

export type RoleN28 = 'metier' | 'po' | 'dev' | 'testeur' | 'release' | 'proprietaire'

/** Rôles N28 (traduits par `n28.role.<rôle>`, miroir vérifié par `verifier_parite_i18n.py`). */
export const LIBELLES_ROLE_N28: Record<RoleN28, string> = {
  metier: 'métier',
  po: 'product owner',
  dev: 'développeur',
  testeur: 'testeur',
  release: 'release manager',
  proprietaire: 'propriétaire',
}

/**
 * Phrases d'action, traduites par `n28.action.<nom>` avec leurs variables (D-11, lot DF) : un
 * navigateur anglais lisait « The développeur can act: trancher la tâche arrêtée … ».
 */
export const ACTIONS_N28 = {
  planMission: 'Approuver le plan de la mission {{objet}}',
  decider: 'Décider : {{objet}}',
  approuver: 'Approuver {{nature}}',
  approuverProjet: 'Approuver {{nature}} — {{projet}}',
  trancherTache: 'Trancher la tâche arrêtée {{objet}}',
  tacheArretee: 'Tâche de colonie arrêtée : {{objet}}',
  verifierAnomalie: 'Vérifier l’anomalie résolue {{objet}}',
  rendreDerogation: 'Rendre la dérogation échue {{objet}}',
  approbationAncienne: 'Décider l’approbation en attente la plus ancienne',
  executerCampagne: 'Exécuter les cas restants de la campagne #{{id}}',
  retablirDeploiement: 'Rétablir le déploiement en échec {{version}} ({{env}})',
  approuverVersion: 'Approuver la version prête {{version}}',
  approuverMiseEnLigne: 'Approuver la mise en ligne de {{version}}',
  budgetErreur: 'Budget d’erreur {{slo}} à {{pct}} %',
  voirApercu: 'Voir l’aperçu de la révision {{revision}}',
  accepterItem: 'Accepter l’item généré « {{titre}} »',
  specifierDemande: 'Spécifier la demande cadrée « {{titre}} »',
  poursuivre: 'Poursuivre « {{titre}} »',
} as const
export type ActionN28 = keyof typeof ACTIONS_N28

/** Natures d'artefact (`n28.nature.<nom>`), dans l'ordre de reconnaissance du chemin. */
export const NATURES_N28 = {
  specification: 'la spécification',
  decision: 'la décision',
  plan: 'le plan',
  design: 'le design',
  backlog: 'le backlog',
  campagne: 'la campagne de tests',
  version: 'la version',
  code: 'le code généré',
  artefact: 'l’artefact',
} as const

type Vars = Record<string, string | number>

/** Phrase française interpolée (repli et tests) ; l'écran traduit `action` + `vars`. */
function phrase(action: ActionN28, vars: Vars = {}): { libelle: string; action: ActionN28; vars: Vars } {
  const libelle = ACTIONS_N28[action].replace(/\{\{(\w+)\}\}/g, (_m, v: string) => String(vars[v] ?? ''))
  return { libelle: libelle.replace(/\s+/g, ' ').trim(), action, vars }
}
/** Ordre de recherche du « rôle qui peut agir » quand rien n'attend le mien. */
export const ORDRE_ROLES: readonly RoleN28[] = ['po', 'dev', 'testeur', 'release', 'metier', 'proprietaire']

/** Libellé de chaque règle, au mot près du § 4.2.2 (2.5 = règle PO ajoutée en v4, AGILE-2-01). */
export const REGLES: Record<RoleN28, Record<number, string>> = {
  metier: { 1: 'recette métier ou mise en ligne à approuver', 2: 'spécification à relire en registre métier', 3: 'aperçu nouveau à voir', 4: 'demande « à voir »' },
  po: { 1: 'plan de mission à approuver', 2: 'approbation en attente', 2.5: 'items de reprise d’une demande de changement approuvée', 3: 'tâche de colonie arrêtée depuis plus de 24 h', 4: 'items générés à accepter', 5: 'demande cadrée non spécifiée', 6: 'demande de changement technologique à examiner' },
  dev: { 1: 'tâche arrêtée à trancher', 2: 'dossier de mission à relire', 3: 'item qui m’est assigné en cours' },
  testeur: { 1: 'anomalie résolue à vérifier', 2: 'campagne ouverte avec des exécutions à faire', 3: 'exigence sans test' },
  release: { 1: 'déploiement en échec', 2: 'approbation de palier en attente', 3: 'version prête non approuvée', 4: 'budget d’erreur ≥ 80 %' },
  proprietaire: { 1: 'dérogation échue', 2: 'demande de changement technologique à approuver', 3: 'migration de gabarit disponible' },
}

export interface Candidat {
  role: RoleN28
  regle: number
  /** Phrase d'action en français (« Approuver le plan de la mission M-7 ») : repli et tests. */
  libelle: string
  /** Phrase à traduire (`n28.action.<action>`) et ses variables ; absente = `libelle` tel quel. */
  action?: ActionN28 | undefined
  vars?: Vars | undefined
  /** Instant d'origine : sert à l'égalité (la plus ancienne gagne) et à « depuis 3 h ». */
  depuis: string
  lien: string
  /** Chemin technique de l'objet (artefact), montré seulement en lien secondaire — jamais en libellé. */
  chemin?: string | undefined
  /** Motif d'un refus avant le clic : l'action est sautée, jamais proposée. */
  refus?: string | null | undefined
  cle: string
}

export interface ResultatN28 {
  role: RoleN28
  action: Candidat | null
  /** Actions du rôle sautées parce que refusées avant le clic (dites, pas cachées). */
  sautees: Candidat[]
  /** Rien pour moi : la première action d'un autre rôle (« le PO peut agir : … »). */
  autreRole: Candidat | null
}

/**
 * Instant d'origine d'un candidat, lu comme toute date de la passerelle (AUD-CRAWL-K1 : le texte
 * PostgreSQL `… +00` était illisible). Illisible : +∞, la date passe après toutes les dates connues.
 */
const instant = (iso: string): number => lireInstant(iso) ?? Number.POSITIVE_INFINITY

/** Ordre : règle croissante, puis la plus ancienne, puis la clé (déterminisme). */
export function comparer(a: Candidat, b: Candidat): number {
  return a.regle - b.regle || instant(a.depuis) - instant(b.depuis) || a.cle.localeCompare(b.cle)
}

function premiere(candidats: readonly Candidat[], role: RoleN28): { action: Candidat | null; sautees: Candidat[] } {
  const duRole = candidats.filter((c) => c.role === role).sort(comparer)
  const action = duRole.find((c) => !c.refus) ?? null
  const sautees = duRole.filter((c) => c.refus && (!action || comparer(c, action) < 0))
  return { action, sautees }
}

export function prochaineAction(role: RoleN28, candidats: readonly Candidat[]): ResultatN28 {
  const { action, sautees } = premiere(candidats, role)
  let autreRole: Candidat | null = null
  if (!action) {
    for (const r of ORDRE_ROLES) {
      if (r === role) continue
      const autre = premiere(candidats, r).action
      if (autre) { autreRole = autre; break }
    }
  }
  return { role, action, sautees, autreRole }
}

/**
 * « 3 h », « 2 j », « 25 min » à l'instant représenté ; rien si la date est illisible (jamais « 0 min »).
 * L'âge se lit à la SECONDE, comme sur une horloge : les microsecondes du texte PostgreSQL ne font pas
 * dire « 29 h » d'une approbation créée à 07:51:03,636 et regardée à 13:51:03 le lendemain.
 */
export function depuisLisible(iso: string, maintenant: Date): string {
  const origine = lireInstant(iso)
  if (origine == null) return ''
  const ms = Math.max(0, Math.floor(maintenant.getTime() / 1000) - Math.floor(origine / 1000)) * 1000
  if (ms >= 2 * 86_400_000) return `${Math.floor(ms / 86_400_000)} j`
  if (ms >= 3_600_000) return `${Math.floor(ms / 3_600_000)} h`
  return `${Math.floor(ms / 60_000)} min`
}

// ---------------------------------------------------------------------------------------------
// Candidats depuis les données déjà lues (points d'accès existants)
// ---------------------------------------------------------------------------------------------

export interface DonneesN28 {
  maintenant: Date
  /** Nom du projet courant : complète le libellé d'une décision sur un artefact. */
  projet?: string | undefined
  /** Identifiant de l'utilisateur courant (pour « que je n'ai pas résolue moi-même »). */
  moi?: string | undefined
  /**
   * Boîte d'attention. Servie par `GET /attention?scope=projet`, chaque élément porte
   * `refusal_code` : l'action refusée AVANT le clic (auto-approbation, non membre…) est sautée.
   */
  attention?: readonly { id: string; kind: string; who?: string; what: string | null; created_at: string; mission_id?: string | null; refusal_code?: string | null }[] | undefined
  /** `/kpi` `governance.oldest_pending_approval_s` : âge de la plus ancienne approbation en attente. */
  plusAncienneApprobationS?: number | null | undefined
  campagnes?: readonly { id: number; status: string; opened_at: string; plan_name?: string }[] | undefined
  deploiements?: readonly { id: string; status: string; environment?: string; version?: string; started_at?: string; finished_at?: string | null }[] | undefined
  versions?: readonly { id: string; version: string; created_at: string; status: string | null; deployed_on?: string | null; readiness?: { ready: boolean } | null }[] | undefined
  apercus?: readonly { revision: string; revision_short: string; app_slug?: string | null; app_name?: string | null; built_at: string | null; created_at: string; previewable: boolean }[] | undefined
  items?: readonly { id: number; kind: string; title: string; status: string; assignee_ref: string | null; acceptance_status: string; generated_by_job: string | null; parent_id: number | null; created_at?: string; updated_at?: string }[] | undefined
  budgetsErreur?: readonly { slo_key?: string; budget_total: number; budget_consumed: number }[] | undefined
  /**
   * O37 (vague 2) — la FILE DE DÉCISIONS servie (`GET /approvals?view=mine`), têtes de lignée
   * seulement, avec `next_step`. Fournie, elle remplace les approbations de la boîte d'attention
   * et le repli du /kpi : la prochaine action pointe la MÊME version que la page de décision,
   * jamais une version remplacée.
   */
  decisions?: readonly {
    id: number; title?: string | null; artifact_path: string; created_at?: string | null
    is_lineage_head?: boolean | null; superseded_by_job_id?: string | null
    next_step?: { can_act: boolean; section: string; code: string } | null
  }[] | undefined
}

const JOUR = 86_400_000

/**
 * Lot NAV 0.5.x (registre n° 9) : cibles d'écran RÉELLES. `/missions/<id>`, `/versions/<id>` et
 * `/apercus` n'ont jamais été des routes (404 « Page introuvable ») : une mission s'ouvre par
 * `/missions?mission=`, une version par `/versions?version=`, une révision par `/apercu?revision=`.
 */
export function lienMission(missionId: string | null | undefined): string {
  return missionId ? `/missions?mission=${encodeURIComponent(missionId)}` : '/missions'
}

export function lienVersion(versionId: string | number | null | undefined): string {
  return versionId != null && String(versionId) !== '' ? `/versions?version=${encodeURIComponent(String(versionId))}` : '/versions'
}

export function lienApercu(p: { revision: string; app_slug?: string | null }): string {
  const q = new URLSearchParams()
  if (p.app_slug) q.set('app', p.app_slug)
  q.set('revision', p.revision)
  return `/apercu?${q.toString()}`
}

/** Motif lisible d'un `refusal_code` de la boîte d'attention (TransverseWorkService). */
export const MOTIFS_REFUS: Record<string, string> = {
  ACCESS_DENIED: 'votre rôle dans le projet ne permet pas cette décision',
  SELF_APPROVAL_REFUSED: 'vous ne pouvez pas approuver votre propre demande',
  DEFECT_VERIFIER_NOT_INDEPENDENT: 'la vérification d’une anomalie revient à une autre personne que celle qui l’a résolue',
  WAIVER_SELF_APPROVAL_REFUSED: 'vous ne pouvez pas décider votre propre dérogation',
}

export function motifRefus(code: string | null | undefined): string | null {
  if (!code) return null
  return MOTIFS_REFUS[code] ?? `refusé avant le clic (${code})`
}

/** Nature d'un artefact d'après son chemin (`artifacts/specs/…/specification.md` → la spécification). */
const NATURES_ARTEFACT: ReadonlyArray<[RegExp, keyof typeof NATURES_N28]> = [
  [/spec/i, 'specification'], [/adr|decision/i, 'decision'], [/plan/i, 'plan'],
  [/design|maquette/i, 'design'], [/backlog|item/i, 'backlog'], [/test|campagne/i, 'campagne'],
  [/release|version/i, 'version'], [/code|patch|diff/i, 'code'],
]

/** Un chemin technique (« a/b/c.md », sans espace) n'est jamais un libellé lisible. */
export function estCheminTechnique(v: string | null | undefined): boolean {
  return Boolean(v) && /[/\\]/.test(v!) && !/\s/.test(v!.trim())
}

/**
 * Libellé humain d'une décision : « Approuver la spécification — Caisse boutique » plutôt que
 * « Décider : artifacts/specs/ws-…/specification.md » ; le chemin passe en lien secondaire.
 */
export function libelleDecision(what: string | null | undefined, projet?: string): {
  libelle: string; action: ActionN28; vars: Vars; chemin?: string
} {
  if (what && !estCheminTechnique(what)) return phrase('decider', { objet: what })
  const nature = what ? (NATURES_ARTEFACT.find(([re]) => re.test(what))?.[1] ?? 'artefact') : 'artefact'
  // La nature est une CLÉ (`n28.nature.<nom>`) : l'écran la traduit avant d'interpoler.
  const p = projet
    ? phrase('approuverProjet', { nature: NATURES_N28[nature], projet })
    : phrase('approuver', { nature: NATURES_N28[nature] })
  const traduite = { ...p, vars: { ...p.vars, nature } }
  return what ? { ...traduite, chemin: what } : traduite
}

export function candidatsDepuisDonnees(d: DonneesN28): Candidat[] {
  const c: Candidat[] = []
  const age = (iso: string): number => d.maintenant.getTime() - instant(iso)
  for (const a of d.attention ?? []) {
    const objet = a.what ?? a.id
    const avant = c.length
    switch (a.kind) {
      case 'mission_plan':
        c.push({ role: 'po', regle: 1, ...phrase('planMission', { objet }), depuis: a.created_at, lien: lienMission(a.mission_id), cle: `att-${a.id}` })
        break
      case 'approval':
        // O37 : la file de décisions, quand elle répond, fait foi (têtes de lignée, lien direct).
        if (d.decisions !== undefined) break
        c.push({ role: 'po', regle: 2, ...libelleDecision(a.what ?? a.id, d.projet), depuis: a.created_at, lien: '/attention', cle: `att-${a.id}` })
        break
      case 'ai_tool_execution':
        c.push({ role: 'po', regle: 2, ...libelleDecision(a.what ?? a.id, d.projet), depuis: a.created_at, lien: '/attention', cle: `att-${a.id}` })
        break
      case 'mission_task_stopped':
      case 'mission_escalation':
        c.push({ role: 'dev', regle: 1, ...phrase('trancherTache', { objet }), depuis: a.created_at, lien: lienMission(a.mission_id), cle: `att-${a.id}` })
        if (age(a.created_at) > JOUR) {
          c.push({ role: 'po', regle: 3, ...phrase('tacheArretee', { objet }), depuis: a.created_at, lien: lienMission(a.mission_id), cle: `att-po-${a.id}` })
        }
        break
      case 'defect_to_verify':
        // « que je n'ai pas résolue moi-même » : `who` porte l'auteur de la résolution.
        if (!d.moi || a.who !== d.moi) {
          c.push({ role: 'testeur', regle: 1, ...phrase('verifierAnomalie', { objet }), depuis: a.created_at, lien: '/anomalies?etat=resolved', cle: `att-${a.id}` })
        }
        break
      case 'waiver_expired':
        c.push({ role: 'proprietaire', regle: 1, ...phrase('rendreDerogation', { objet }), depuis: a.created_at, lien: '/exigences', cle: `att-${a.id}` })
        break
      default:
        break
    }
    const refus = motifRefus(a.refusal_code)
    if (refus) for (let i = avant; i < c.length; i += 1) c[i] = { ...c[i]!, refus }
  }
  // O37 : une décision de la file = une TÊTE de lignée que je peux faire maintenant, lien direct.
  for (const a of d.decisions ?? []) {
    if (a.is_lineage_head === false || a.superseded_by_job_id) continue
    if (!a.next_step?.can_act || a.next_step.section !== 'mine') continue
    const titre = a.title?.trim()
    c.push({
      role: 'po', regle: 2, ...(titre ? phrase('decider', { objet: titre }) : libelleDecision(a.artifact_path, d.projet)),
      depuis: a.created_at ?? d.maintenant.toISOString(), lien: `/approvals/${a.id}`, cle: `dec-${a.id}`,
    })
  }
  // Décisions : la gouvernance du /kpi date la plus ancienne approbation en attente, même quand la
  // boîte d'attention ne l'a pas (encore) listée ; jamais en double d'une approbation déjà vue.
  // O37 : sans objet quand la file de décisions a répondu — elle seule dit ce qui m'attend.
  if (d.decisions === undefined && d.plusAncienneApprobationS != null && !c.some((x) => x.role === 'po' && x.regle === 2)) {
    c.push({
      role: 'po', regle: 2, ...phrase('approbationAncienne'),
      depuis: new Date(d.maintenant.getTime() - d.plusAncienneApprobationS * 1000).toISOString(), lien: '/attention', cle: 'kpi-approbation',
    })
  }
  for (const k of d.campagnes ?? []) {
    if (k.status !== 'open') continue
    c.push({ role: 'testeur', regle: 2, ...phrase('executerCampagne', { id: k.id }), depuis: k.opened_at, lien: `/tests?vue=campagnes&campagne=${k.id}`, cle: `camp-${k.id}` })
  }
  // Release 1 : dernier déploiement d'un environnement en échec.
  const derniers = new Map<string, NonNullable<DonneesN28['deploiements']>[number]>()
  for (const x of d.deploiements ?? []) {
    const env = x.environment ?? '?'
    const t = instant(x.finished_at ?? x.started_at ?? '')
    const connu = derniers.get(env)
    if (!connu || t > instant(connu.finished_at ?? connu.started_at ?? '')) derniers.set(env, x)
  }
  for (const x of derniers.values()) {
    if (/fail|error|echec/i.test(x.status)) {
      c.push({ role: 'release', regle: 1, ...phrase('retablirDeploiement', { version: x.version ?? '', env: x.environment ?? '' }), depuis: x.finished_at ?? x.started_at ?? '', lien: '/runops', cle: `dep-${x.id}` })
    }
  }
  for (const v of d.versions ?? []) {
    if (v.readiness?.ready && !v.deployed_on) {
      c.push({ role: 'release', regle: 3, ...phrase('approuverVersion', { version: v.version }), depuis: v.created_at, lien: lienVersion(v.id), cle: `ver-${v.id}` })
      c.push({ role: 'metier', regle: 1, ...phrase('approuverMiseEnLigne', { version: v.version }), depuis: v.created_at, lien: lienVersion(v.id), cle: `ver-m-${v.id}` })
    }
  }
  for (const b of d.budgetsErreur ?? []) {
    if (b.budget_total > 0 && b.budget_consumed / b.budget_total >= 0.8) {
      c.push({ role: 'release', regle: 4, ...phrase('budgetErreur', { slo: b.slo_key ?? '', pct: Math.round((b.budget_consumed / b.budget_total) * 100) }), depuis: d.maintenant.toISOString(), lien: '/exploitation', cle: `be-${b.slo_key ?? ''}` })
    }
  }
  // O37 : seule la DERNIÈRE révision prévisualisable de chaque application est proposée — un
  // aperçu d'une révision remplacée n'est plus « à voir ».
  const dernieres = new Map<string, NonNullable<DonneesN28['apercus']>[number]>()
  for (const p of d.apercus ?? []) {
    if (!p.previewable) continue
    const app = p.app_slug ?? p.app_name ?? ''
    const connu = dernieres.get(app)
    if (!connu || instant(p.built_at ?? p.created_at) > instant(connu.built_at ?? connu.created_at)) dernieres.set(app, p)
  }
  for (const p of dernieres.values()) {
    // « Nouveau » : construit depuis moins de 7 jours (le « déjà vu » n'est pas mémorisé côté serveur).
    const construit = p.built_at ?? p.created_at
    if (p.previewable && age(construit) < 7 * JOUR) {
      c.push({ role: 'metier', regle: 3, ...phrase('voirApercu', { revision: p.revision_short }), depuis: construit, lien: lienApercu(p), cle: `ap-${p.revision}` })
    }
  }
  const items = d.items ?? []
  const parents = new Set(items.map((i) => i.parent_id).filter((x): x is number => x != null))
  for (const i of items) {
    const depuis = i.updated_at ?? i.created_at ?? ''
    if (i.acceptance_status === 'pending' && i.generated_by_job) {
      c.push({ role: 'po', regle: 4, ...phrase('accepterItem', { titre: i.title }), depuis, lien: `/projet?vue=tableau&item=${i.id}`, cle: `item-acc-${i.id}` })
    }
    if (i.kind === 'cadrage' && i.status === 'done' && !parents.has(i.id)) {
      c.push({ role: 'po', regle: 5, ...phrase('specifierDemande', { titre: i.title }), depuis, lien: `/projet?vue=tableau&item=${i.id}`, cle: `item-cad-${i.id}` })
    }
    if (d.moi && i.assignee_ref === d.moi && i.status === 'in_progress') {
      c.push({ role: 'dev', regle: 3, ...phrase('poursuivre', { titre: i.title }), depuis, lien: `/projet?vue=tableau&item=${i.id}`, cle: `item-dev-${i.id}` })
    }
  }
  return c
}
